import { useState, useEffect, useRef } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { fetchPetAvatar, getAvatarUrl, uploadPetAvatar } from '../utils/petPhotoUtils'
import ImageCropDialog from './ImageCropDialog'

/**
 * Reusable circular pet avatar component with optional upload capability
 * 
 * @param {string} petId - UUID of the pet (null for new pets)
 * @param {string} merchantId - UUID of the merchant (required for upload)
 * @param {string} clientId - UUID of the client (optional)
 * @param {number} size - Avatar size in pixels (default: 80)
 * @param {boolean} editable - Enable upload functionality
 * @param {Function} onAvatarChange - Callback when avatar changes (for new pets, receives blob)
 * @param {string} pendingAvatarUrl - Preview URL for pending avatar (new pets)
 * @param {Function} onUploadComplete - Callback after successful upload with {photo, url}
 * @param {boolean} hideIfEmpty - If true, hide component when no avatar exists (default: false)
 * @param {string} preloadedAvatarUrl - Pre-fetched avatar URL (skips DB fetch if provided)
 */
function PetAvatar({
	petId,
	merchantId,
	clientId,
	size = 80,
	editable = false,
	onAvatarChange,
	pendingAvatarUrl,
	onUploadComplete,
	hideIfEmpty = false,
	preloadedAvatarUrl = undefined
}) {
	const [avatarUrl, setAvatarUrl] = useState(preloadedAvatarUrl || null)
	const [photoRecord, setPhotoRecord] = useState(null)
	// If preloaded URL is provided, skip loading state
	const [loading, setLoading] = useState(preloadedAvatarUrl === undefined)
	const [hasLoaded, setHasLoaded] = useState(preloadedAvatarUrl !== undefined)
	const [uploading, setUploading] = useState(false)
	const [cropDialogOpen, setCropDialogOpen] = useState(false)
	const [selectedFile, setSelectedFile] = useState(null)
	const fileInputRef = useRef(null)

	// Fetch existing avatar when petId changes (only if not preloaded)
	useEffect(() => {
		// If preloadedAvatarUrl is provided (even if null), don't fetch
		if (preloadedAvatarUrl !== undefined) {
			setAvatarUrl(preloadedAvatarUrl || null)
			setLoading(false)
			setHasLoaded(true)
			return
		}

		const loadAvatar = async () => {
			if (!petId) return

			setLoading(true)
			try {
				const photo = await fetchPetAvatar(petId)
				if (photo) {
					setPhotoRecord(photo)
					setAvatarUrl(getAvatarUrl(photo))
				} else {
					setAvatarUrl(null)
					setPhotoRecord(null)
				}
			} catch (error) {
				console.error('Error loading avatar:', error)
			} finally {
				setLoading(false)
				setHasLoaded(true)
			}
		}

		if (petId) {
			loadAvatar()
		} else {
			setAvatarUrl(null)
			setPhotoRecord(null)
			setLoading(false)
			setHasLoaded(true)
		}
	}, [petId, preloadedAvatarUrl])

	const handleClick = () => {
		if (editable && fileInputRef.current) {
			fileInputRef.current.click()
		}
	}

	const handleFileSelect = (event) => {
		const file = event.target.files?.[0]
		if (file && file.type.startsWith('image/')) {
			setSelectedFile(file)
			setCropDialogOpen(true)
		}
		// Reset input so same file can be selected again
		event.target.value = ''
	}

	const handleCropComplete = async (croppedBlob) => {
		setCropDialogOpen(false)
		setSelectedFile(null)

		if (!petId) {
			// New pet - notify parent with blob for later upload
			if (onAvatarChange) {
				// Create preview URL
				const previewUrl = URL.createObjectURL(croppedBlob)
				onAvatarChange(croppedBlob, previewUrl)
			}
			return
		}

		// Existing pet - upload immediately
		setUploading(true)
		try {
			const result = await uploadPetAvatar(
				petId,
				merchantId,
				clientId,
				croppedBlob,
				photoRecord?.id || null
			)

			if (result) {
				setPhotoRecord(result.photo)
				setAvatarUrl(result.url)
				if (onUploadComplete) {
					onUploadComplete(result)
				}
			}
		} catch (error) {
			console.error('Error uploading avatar:', error)
		} finally {
			setUploading(false)
		}
	}

	const handleCropCancel = () => {
		setCropDialogOpen(false)
		setSelectedFile(null)
	}

	// Determine which URL to display
	const displayUrl = pendingAvatarUrl || avatarUrl

	// Hide component if hideIfEmpty is true and no avatar exists (after loading)
	if (hideIfEmpty && hasLoaded && !displayUrl && !loading) {
		return null
	}

	return (
		<>
			<Box
				onClick={handleClick}
				sx={{
					width: size,
					height: size,
					borderRadius: '50%',
					bgcolor: '#E9D8FD',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					position: 'relative',
					overflow: 'hidden',
					cursor: editable ? 'pointer' : 'default',
					flexShrink: 0,
					'&:hover': editable ? {
						opacity: 0.9,
						'& .camera-overlay': {
							opacity: 1
						}
					} : {}
				}}
			>
				{(loading || uploading) ? (
					<CircularProgress size={size * 0.4} sx={{ color: '#9F7AEA' }} />
				) : displayUrl ? (
					<img
						src={displayUrl}
						alt="Pet avatar"
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover'
						}}
					/>
				) : (
					// Default pet icon placeholder
					<div style={{ width: size, height: size, backgroundColor: '#D9D9D9' }} />
				)}

				{/* Camera overlay for editable mode */}
				{editable && (
					<Box
						className="camera-overlay"
						sx={{
							position: 'absolute',
							bottom: 0,
							left: 0,
							right: 0,
							height: '40%',
							bgcolor: 'rgba(0, 0, 0, 0.5)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							opacity: displayUrl ? 0 : 1,
							transition: 'opacity 0.2s'
						}}
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M15.3333 12.6667C15.3333 13.0203 15.1928 13.3594 14.9428 13.6095C14.6927 13.8595 14.3536 14 14 14H2C1.64638 14 1.30724 13.8595 1.05719 13.6095C0.807143 13.3594 0.666664 13.0203 0.666664 12.6667V5.33333C0.666664 4.97971 0.807143 4.64057 1.05719 4.39052C1.30724 4.14048 1.64638 4 2 4H4.66666L6 2H10L11.3333 4H14C14.3536 4 14.6927 4.14048 14.9428 4.39052C15.1928 4.64057 15.3333 4.97971 15.3333 5.33333V12.6667Z"
								stroke="white"
								strokeWidth="1.33333"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M8 11.3333C9.47276 11.3333 10.6667 10.1394 10.6667 8.66667C10.6667 7.19391 9.47276 6 8 6C6.52724 6 5.33334 7.19391 5.33334 8.66667C5.33334 10.1394 6.52724 11.3333 8 11.3333Z"
								stroke="white"
								strokeWidth="1.33333"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</Box>
				)}
			</Box>

			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				onChange={handleFileSelect}
				style={{ display: 'none' }}
			/>

			{/* Image crop dialog */}
			<ImageCropDialog
				open={cropDialogOpen}
				onClose={handleCropCancel}
				imageFile={selectedFile}
				onCropComplete={handleCropComplete}
			/>
		</>
	)
}

export default PetAvatar

